import { forwardRef } from 'react';
import type { PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';

import Flex from '../Flex';
import { useI18n } from '../../hooks';
import { defaultThemeProp } from '../../theme';
import type { BaseProps, ForwardRefForwardPropsComponent, NoChildrenProp } from '../../types';
import Icon, { StyledIcon } from '../Icon';

export interface SentimentProps extends BaseProps, NoChildrenProp {
  /** Ref to the element */
  ref?: Ref<HTMLDivElement>;
  /** Variant of the sentiment */
  variant: 'positive' | 'neutral' | 'negative';
  /** Flag that indicates if label needs to be hidden
   * @default false
   */
  labelHidden?: boolean;
  /** Labels for different sentiment variants. If not supplied default labels will be shown */
  labels?: {
    [variant in SentimentProps['variant']]?: string;
  };
  /** Icons for different sentiment variants. If not supplied default icons will be shown */
  icons?: {
    [variant in SentimentProps['variant']]?: string;
  };
  /** Aria label for screen readers. */
  'aria-label'?: string;
}

const defaultIcons = {
  negative: 'face-sad',
  positive: 'face-happy',
  neutral: 'face-blank'
} satisfies SentimentProps['icons'];

export const StyledSentiment = styled.div<SentimentProps>(props => {
  const { theme, variant } = props;
  return css`
    > ${StyledIcon} {
      color: ${theme.components.sentiment[variant].color};
    }
  `;
});

StyledSentiment.defaultProps = defaultThemeProp;

const Sentiment: ForwardRefForwardPropsComponent<SentimentProps> = forwardRef(function Sentiment(
  props: PropsWithoutRef<SentimentProps>,
  ref: SentimentProps['ref']
) {
  const t = useI18n();

  const defaultLabels = {
    negative: t('sentiment_negative'),
    positive: t('sentiment_positive'),
    neutral: t('sentiment_neutral')
  } satisfies SentimentProps['labels'];

  const {
    'aria-label': ariaLabel,
    variant,
    labelHidden = false,
    labels = {},
    icons = {},
    ...restProps
  } = props;

  return (
    <Flex
      aria-label={ariaLabel ?? labels[variant] ?? defaultLabels[variant]}
      ref={ref}
      variant={variant}
      role='img'
      {...restProps}
      as={StyledSentiment}
      container={{ gap: 1 }}
    >
      <Icon aria-hidden name={icons[variant] ?? defaultIcons[variant]} />
      {!labelHidden && <span>{labels[variant] ?? defaultLabels[variant]}</span>}
    </Flex>
  );
});

export default Sentiment;
